import { Close, CollapseAll, ExpandAll, Filter, Plus, Refresh, Search } from '../icons';

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import FilterPopover from '../filterPopover/filterPopover';
import FavouriteCta from '../favouriteCta/favouriteCta';
import Tag from '../tag/tag';
import { getAppliedFilterState } from '../../helpers/appliedFilterHelper';
import { IFilterOption } from '../../interface/transaction';
import { Button, IconOutlineButton, InputSearch, Tooltip } from '../Ant';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import { withRefreshToast } from '../utils/toast';

const SearchInputWithFilters = ({
  filtersDisabledInfoMessage,
  disableFilter = false,
  handleSearch,
  handleRefresh,
  ownerOptions = [],
  ownerTitle = '',
  setSelectedOwner,
  radioOptions = [],
  selectedFilter = [],
  setSelectedFilter,
  clearFilter,
  setSelectedCheckboxFilter,
  checkboxOptions = [],
  filterTitle = '',
  radioTitle = '',
  checkboxTitle = '',
  searchTitle = '',
  handleAdd,
  tooltipTextForAdd,
  focusAddOnEntry = false,
  isFavouriteFilterActive,
  handleFavouriteToggle,
  searchValue = '',
  autoFocus = false,
  tabToResults = false,
  disabledCheckboxValues = [],
  disabledRadioValues = [],
  hasMoreCheckboxOptions,
  isFetchingMoreCheckboxOptions,
  onLoadMoreCheckboxOptions,
  dateGroupToggle,
  children,
}: {
  filtersDisabledInfoMessage?: string;
  disableFilter?: boolean;
  handleSearch: (val: string) => void;
  // Returning the refetch promise keeps the refreshing toast up for as long as the refresh runs.
  handleRefresh?: () => void | Promise<unknown>;
  ownerOptions?: IFilterOption[];
  ownerTitle?: string;
  setSelectedOwner?: (val: IFilterOption) => void;
  radioOptions?: IFilterOption[];
  checkboxOptions?: IFilterOption[];
  selectedFilter?: IFilterOption[];
  setSelectedFilter?: (val: IFilterOption) => void;
  setSelectedCheckboxFilter?: (val: IFilterOption[]) => void;
  clearFilter?: (val?: string) => void;
  filterTitle?: string;
  searchTitle?: string;
  radioTitle?: string;
  checkboxTitle?: string;
  handleAdd?: () => void;
  tooltipTextForAdd?: string;
  focusAddOnEntry?: boolean;
  isFavouriteFilterActive?: boolean;
  handleFavouriteToggle?: (value: boolean) => void;
  searchValue?: string;
  autoFocus?: boolean;
  tabToResults?: boolean;
  disabledCheckboxValues?: string[];
  disabledRadioValues?: string[];
  hasMoreCheckboxOptions?: boolean;
  isFetchingMoreCheckboxOptions?: boolean;
  onLoadMoreCheckboxOptions?: () => void;
  dateGroupToggle?: { areAllExpanded: boolean; onToggleAll: () => void };
  children?: React.ReactNode;
}) => {
  const [inputValue, setInputValue] = useState(searchValue);

  useEffect(() => {
    setInputValue(searchValue);
  }, [searchValue]);

  const searchFunction = useCallback((query: string, abortSignal: AbortSignal) => {
    // Check if the request was aborted before proceeding
    if (abortSignal.aborted) return;
    handleSearch(query);
  }, []);

  const { search } = useDebouncedSearch(searchFunction, {
    debounceDelay: 400,
    allowEmptySearch: true,
  });

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value ?? '';
    setInputValue(value);
    search(value);
  };

  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();

  const toggleAllLabel = t(
    dateGroupToggle?.areAllExpanded ? 'common:collapseAll' : 'common:expandAll',
  );
  // With no explicit disabledRadioValues, `disableFilter` leaves no radio option the user could
  // pick. The trigger is only locked when no checkbox option is left to offer either.
  const isFilterFullyLocked =
    disableFilter && disabledRadioValues.length === 0 && checkboxOptions.length === 0;

  const { appliedFilters, isTagLocked, isClearAllDisabled } = getAppliedFilterState({
    selectedFilter,
    checkboxOptions,
    disableFilter,
    disabledCheckboxValues,
  });

  const filterButton = (
    <IconOutlineButton
      icon={<Filter className="fz-16" />}
      data-testid="filter-button"
      aria-label={t('common:filters')}
      aria-haspopup="dialog"
      aria-expanded={visible}
      disabled={isFilterFullyLocked}
      className={[appliedFilters.length > 0 ? 'filter-button' : '', visible ? 'active' : '']
        .filter(Boolean)
        .join(' ')}
    />
  );

  return (
    <div className="d-flex fd-column gap-gutter w-full">
      {/* CTA Buttons */}
      <div className="cta-wrapper d-flex items-center gap-5 w-full ">
        <InputSearch
          placeholder={searchTitle}
          prefix={<Search />}
          onChange={onSearch}
          value={inputValue}
          autoFocus={autoFocus}
          className="w-full"
          data-testid="search-input"
          data-list-search
          data-tab-to-results={tabToResults || undefined}
          size="small"
          allowClear={{
            clearIcon: <Close className="custom-clear-icon" />,
          }}
        />

        {handleRefresh && (
          <Tooltip title={t('common:refresh')} arrow={false}>
            <IconOutlineButton
              icon={<Refresh className="fz-14" />}
              onClick={() => {
                // The refresh runs fire-and-forget, so its rejection has to be caught here to
                // avoid an unhandled promise. The user sees the failure through the list's own
                // error state; this only keeps the cause out of the dark.
                void withRefreshToast(handleRefresh).catch((error) => {
                  console.error('Error refreshing the list:', error);
                });
              }}
              className="topbar-cta"
              data-testid="refresh-button"
              aria-label={t('common:refresh')}
            />
          </Tooltip>
        )}
        {(checkboxOptions?.length > 0 || radioOptions?.length > 0 || ownerOptions.length > 0) && (
          <div>
            {isFilterFullyLocked ? (
              <Tooltip
                title={filtersDisabledInfoMessage ?? t('common:filters')}
                arrow={false}
                placement="bottomRight"
              >
                {/* A disabled button emits no mouse events, so the tooltip listens on the wrapper */}
                <span className="d-flex" data-testid="filter-button-wrapper">
                  {filterButton}
                </span>
              </Tooltip>
            ) : (
              <FilterPopover
                disabled={disableFilter}
                popoverTitle={filterTitle}
                primarytitle={radioTitle}
                secondarytitle={checkboxTitle}
                ownerTitle={ownerTitle}
                ownerOptions={ownerOptions}
                radioOptions={radioOptions}
                checkboxOptions={checkboxOptions}
                visible={visible}
                onOpenChange={setVisible}
                selectedValue={selectedFilter}
                setSelectedValue={(val) => setSelectedFilter?.(val)}
                setSelectedOwner={(val) => setSelectedOwner?.(val)}
                setCheckBoxValues={(val) => {
                  setSelectedCheckboxFilter?.(val);
                }}
                onClear={() => {
                  clearFilter?.();
                }}
                disabledCheckboxValues={disabledCheckboxValues}
                disabledRadioValues={disabledRadioValues}
                disabledInfoMessage={filtersDisabledInfoMessage}
                hasMoreCheckboxOptions={hasMoreCheckboxOptions}
                isFetchingMoreCheckboxOptions={isFetchingMoreCheckboxOptions}
                onLoadMoreCheckboxOptions={onLoadMoreCheckboxOptions}
              >
                <Tooltip title={t('common:filters')} arrow={false}>
                  {filterButton}
                </Tooltip>
              </FilterPopover>
            )}
          </div>
        )}

        {dateGroupToggle && (
          <Tooltip title={toggleAllLabel} arrow={false}>
            <IconOutlineButton
              icon={
                dateGroupToggle.areAllExpanded ? (
                  <ExpandAll className="fz-16" />
                ) : (
                  <CollapseAll className="fz-16" />
                )
              }
              onClick={dateGroupToggle.onToggleAll}
              aria-label={toggleAllLabel}
              className="topbar-cta"
              data-testid="toggle-all-date-groups-button"
            />
          </Tooltip>
        )}

        {handleFavouriteToggle && (
          <FavouriteCta isFavourite={!!isFavouriteFilterActive} onToggle={handleFavouriteToggle} />
        )}

        {handleAdd && (
          <Tooltip title={tooltipTextForAdd} arrow={false}>
            <Button
              data-testid="add-button"
              data-entry-focus={focusAddOnEntry || undefined}
              aria-label={tooltipTextForAdd}
              block
              maxwidth="30px"
              icon={<Plus className="fs-18" />}
              type="primary"
              onClick={handleAdd}
            />
          </Tooltip>
        )}
      </div>

      {children}

      {/* Tag and Clear Filters */}
      {appliedFilters.length > 0 && (
        <div className="d-flex justify-between items-center w-full applied-filter-wrapper">
          <div className="d-flex gap-5 items-center flex-wrap">
            {appliedFilters.map((val) => {
              return (
                <Tag
                  data-testid={`selected-filter-${val.value}`}
                  key={val.value}
                  text={val.label}
                  disabled={isTagLocked(val.value)}
                  disabledInfoMessage={filtersDisabledInfoMessage}
                  removeTag={() => {
                    clearFilter?.(val.value);
                  }}
                />
              );
            })}
          </div>
          <Button
            data-testid="clear-filters-button"
            type="text"
            className="filter-cta"
            disabled={isClearAllDisabled}
            onClick={() => {
              setVisible(false);
              clearFilter?.();
            }}
          >
            {t('common:filter.clearAll')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SearchInputWithFilters;
